import './Events.scss';

import MuiAlert, {
	type AlertColor,
	type AlertProps,
} from '@mui/material/Alert';
import {
	useCreateEvent,
	useDeleteEventRecord,
	useDeleteEvents,
	useUpdateEvent,
} from 'features/events/hooks/event-hooks';
import {
	initialEventFormState,
	selectEventsFormData,
	selectSelectedEvents,
	setEventsFormRef,
	setIsEventNameReadonly,
	setIsEventsFormDirty,
	setSelectedEvents,
	updateEventsFormValues,
} from 'features/events/redux/events-form-slice';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { Snackbar } from '@mui/material';
import { Modal } from 'components/Modal/Modal';
import { useModal } from 'components/Modal/useModal';
import RouterPromptModal from 'components/RouterPromptModal/RouterPromptModal';
import { TwoPaneLayout } from 'components/two-pane-layout/two-pane-layout';
import { usePropertyContext } from 'context/propertyContext';
import { alertAdded } from 'features/alerts/redux/alerts-slice';
import { today } from 'features/dates/date-helpers';
import { useUser } from 'features/users/context/userContext';
import type { EventCategoryEnum } from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import EventsForm from '../Events/EventsForm';
import { EventList } from './EventList';

export type MessageType = {
	message: string;
	messageType: AlertColor;
};

export interface EventValues {
	name: string;
	recurs: boolean;
	category: EventCategoryEnum;
	occ_impact: number;
	property_id: string;
	created_by_id: string;
	created_at: string;
	updated_by_id: string;
	updated_at: string;
}

export interface EventFormValues {
	id: string | null;
	event_id: string;
	start_date: string;
	end_date: string;
	event: EventValues;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
	return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />;
});

export const Events = () => {
	const { createEvent, createEventLoading } = useCreateEvent();
	const { updateEvent, updateEventLoading } = useUpdateEvent();
	const [loading, setLoading] = useState(true);
	const [alertMessage, setAlertMessage] = useState<MessageType | null>(null);

	const { user } = useUser();

	const {
		property: { propertyId },
	} = usePropertyContext();

	useEffect(() => {
		setTimeout(() => setLoading(false), 1000);
	}, []);

	const dispatch = useAppDispatch();
	const { deleteEvents, deleteEventsLoading } = useDeleteEvents();
	const { deleteEventRecord, deleteEventRecordLoading } =
		useDeleteEventRecord();
	const [isAlertMessageVisible, setIsAlertMessageVisible] = useState(false);
	const [isBlocked, setIsBlocked] = useState(false);
	const { isShown, toggle } = useModal();

	const eventsFormRef = useRef<any>(null);

	const selectedEvents = useAppSelector(selectSelectedEvents);
	useEffect(() => {
		dispatch(setEventsFormRef(eventsFormRef.current));
		dispatch(setIsEventsFormDirty(eventsFormRef.current?.dirty || false));
	}, [eventsFormRef, dispatch]);

	const onAlertMessageClose = () => {
		setIsAlertMessageVisible(false);
	};

	const onDelete = () => {
		const selectedEvent: any = eventsFormRef?.current?.values;
		deleteEvents(selectedEvent, () => {
			toggle();
			const alertMessage = `${selectedEvent.name} ${labels.events.delete_single_success_msg}`;
			dispatch(alertAdded('success', alertMessage));
			eventsFormRef.current?.resetForm({
				values: initialEventFormState.formValues,
			});
			dispatch(setSelectedEvents([]));
			setIsBlocked(false);
		});
	};

	const onRecordDelete = (record: any) => {
		if (record.start_date && record.end_date) {
			deleteEventRecord(record, () => {
				const alertMessage = `${labels.events.delete_event_record_success_msg}`;
				dispatch(alertAdded('success', alertMessage));
			});
		}
	};

	const onFormReset = useCallback(async () => {
		dispatch(setIsEventNameReadonly(false));
		eventsFormRef.current?.resetForm({
			values: initialEventFormState.formValues,
		});
		dispatch(updateEventsFormValues({ ...initialEventFormState.formValues }));
		setIsBlocked(false);
	}, [dispatch]);

	const onSaveClick = async () => {
		const values: any = eventsFormRef?.current?.values;
		if (values?.event_id) {
			const updatedEvent = await updateEvent(
				{
					id: values.id,
					event_id: values.event_id,
					name: values.name,
					category: values.category,
					records: values.records,
					occ_impact: values.occ_impact,
					recurs: values.records.length > 1,
					created_by_id: user?.id,
					created_at: today(),
					updated_by_id: user?.id,
					updated_at: today(),
					property_id: propertyId,
				},
				propertyId,
			);
			if (updatedEvent?.success) {
				setAlertMessage({
					messageType: 'success',
					message: labels.events.create_confirmation_msg,
				});
				setIsBlocked(false);
			}
		} else {
			const newSeason = await createEvent({
				name: values.name,
				category: values.category,
				records: values.records,
				occ_impact: values.occ_impact,
				recurs: values.records.length > 1,
				created_by_id: user?.id,
				created_at: today(),
				updated_by_id: user?.id,
				updated_at: today(),
				property_id: propertyId,
			});
			if (newSeason?.success) {
				setAlertMessage({
					messageType: 'success',
					message: labels.events.modify_confirmation_msg,
				});
				setIsBlocked(false);
			}
		}
	};

	const formData = useAppSelector(selectEventsFormData);

	return (
		<div className='events-container'>
			<RouterPromptModal isBlocked={isBlocked} />
			<div className='error'>
				<Snackbar
					className='error-msg'
					open={isAlertMessageVisible}
					autoHideDuration={3000}
					onClose={onAlertMessageClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					<Alert
						onClose={onAlertMessageClose}
						severity={alertMessage?.messageType}
						sx={{ width: '100%' }}
					>
						{alertMessage?.message}
					</Alert>
				</Snackbar>
			</div>

			<Modal
				isShown={isShown}
				hide={toggle}
				onConfirm={onDelete}
				onCancel={toggle}
				loading={deleteEventsLoading}
				isConfirmationModal
				hasIcon
				headerText={labels.events.modal_header_text}
				confirmBtnText={labels.common.confirm}
				cancelBtnText={labels.common.cancel}
				messageBody={`${labels.events.confirmation_message}\n${selectedEvents
					.map((e) => e?.event?.name)
					.join('\n')}`}
			/>

			<TwoPaneLayout
				loading={
					loading ||
					updateEventLoading ||
					createEventLoading ||
					deleteEventRecordLoading
				}
				LeftSideComponent={
					<EventsForm
						formRef={eventsFormRef}
						id='events-form'
						onFormReset={onFormReset}
						readonly={false}
						formData={formData}
						onSaveClick={onSaveClick}
						onDeleteClick={toggle}
						onRecordDelete={onRecordDelete}
						setIsBlocked={setIsBlocked}
					/>
				}
				RightSideComponent={
					<EventList
						// onEventCopy={onEventCopy} // if you re-enable that
						onFormReset={onFormReset}
					/>
				}
			/>
		</div>
	);
};
